/* eslint-disable react/require-default-props */
/* eslint-disable no-param-reassign */
/* eslint-disable react/destructuring-assignment */
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  Input,
  Modal,
  Paper,
  Space,
  Switch,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconDeviceFloppy, IconNotes, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useRef } from 'react';
import { validarReferenciaServicoCompartilhado } from '../../../../business/proposals/validation';
import BasicPricing, { BasicPricingData, BasicPricingRef } from '../../../../components/BasicPricing';
import BasicValueUnits, {
  BasicValueUnitsData,
  BasicValueUnitsRef,
} from '../../../../components/BasicValueUnits';
import PackagingPicker from '../../../../components/core/PackagingPicker/PackagingPicker';
import PageSection from '../../../../components/core/PageSection/PageSection';
import ProposalServiceList from '../../../../components/core/ProposalServiceList/ProposalServiceList';
import { UnitOfMeasureType } from '../../../../models/core/cache.type';
import { Action } from '../../../../models/core/core.type';
import { PackagingType } from '../../../../models/core/packaging.type';
import {
  ProposalResiduePlanPackagingType,
  ProposalResiduePlanQuotationPackagingType,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanType,
  ProposalServiceType,
  ProposalTipoPrecificacaoEnum,
} from '../../../../models/core/proposals.type';
import { Feature, Financial } from '../../../../utils/constants.utils';
import { buildFakeAuditObject, isNullOrEmpty, newGuid } from '../../../../utils/helper.utils';
import DeleteConfirmPopover from '../../../../components/DeleteConfirmPopover';
import ResidueQuotationPackagingForm, {
  ResidueQuotationPackagingFormRef,
} from './ResidueQuotationPackagingForm';

type FormViewData = {
  action: Action;
  id: string;
  idResiduoAcondicionamento: number | null;
  residuoAcondicionamento: string;
  residuoAcondicionamentoDescricao: string | null;
  quantidade: number | null;
  quantidadeIdUnidadeMedida: string | null;
  compartilhado: boolean;
  gerenciar: boolean;
  propostaServicoData: ProposalServiceType | null;
  idPropostaServico: number | null;
  idPropostaServicoTemp: string | null;
  propostaServico: string | null;
  precificacao: BasicPricingData;
  observacao: string | null;
};

type ResiduePlanPackagingModalProps = {
  opened: boolean;
  onClose: () => void;
  modoEdicao: boolean;
  requireSupplier?: boolean;
  referenceData: {
    unitOfMeasures: UnitOfMeasureType[];
  };
  services: ProposalServiceType[];
  plan?: ProposalResiduePlanType | null;
  idEntidadeEndereco: number;
  origItem: ProposalResiduePlanPackagingType | null;
  item: ProposalResiduePlanPackagingType | null;
  idPropostaResiduoPlano?: number;
  zIndex: number;
  callback?: (
    item: ProposalResiduePlanPackagingType | null,
    action: string,
    confirmed: boolean
  ) =>
    | ProposalResiduePlanPackagingType
    | null
    | void
    | Promise<ProposalResiduePlanPackagingType | null | void>;
  onAddQuotation?: (plan: ProposalResiduePlanType) => void;
  onSetPrimaryQuotation?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType
  ) => void;
  onDeleteQuotation?: (plan: ProposalResiduePlanType, quotation: ProposalResiduePlanQuotationType) => void;
  onQuotationSubmit?: (
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanPackagingType,
    item: ProposalResiduePlanQuotationPackagingType | null,
    action: string
  ) => void;
};

const buildServiceFromItem = (
  item: ProposalResiduePlanPackagingType | null,
  services: ProposalServiceType[],
  idEntidadeEndereco: number
) => {
  let service = services.find(
    (x) =>
      (item?.idPropostaServico && x.idPropostaServico === item.idPropostaServico) ||
      (item?.idPropostaServicoTemp && x.id === item.idPropostaServicoTemp)
  );
  if (
    !validarReferenciaServicoCompartilhado(item?.idResiduoAcondicionamento || -1, idEntidadeEndereco, service)
  ) {
    service = undefined;
  }
  return service || null;
};

const buildInitialValues = (
  item: ProposalResiduePlanPackagingType | null,
  origItem: ProposalResiduePlanPackagingType | null,
  unitOfMeasures: UnitOfMeasureType[],
  services: ProposalServiceType[],
  idEntidadeEndereco: number
): FormViewData => {
  const service = buildServiceFromItem(item, services, idEntidadeEndereco);
  return {
    action: origItem ? Action.Nothing : Action.Add,
    id:
      item?.id || item?.idTemporaria || item?.idPropostaResiduoPlanoAcondicionamento?.toString() || newGuid(),
    idResiduoAcondicionamento: item?.idResiduoAcondicionamento || null,
    residuoAcondicionamento: item?.residuoAcondicionamento || '',
    residuoAcondicionamentoDescricao: item?.residuoAcondicionamentoDescricao || null,
    quantidade: item?.compartilhado ? (item?.quantidade || 0) * 100 : item?.quantidade || 1,
    quantidadeIdUnidadeMedida:
      item?.quantidadeIdUnidadeMedida?.toString() ||
      unitOfMeasures
        .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
        ?.idUnidadeMedida.toString() ||
      null,
    compartilhado: item ? item.compartilhado : false,
    gerenciar: item ? item.gerenciar : true,
    propostaServicoData: service,
    idPropostaServico: item?.idPropostaServico || null,
    idPropostaServicoTemp: item?.idPropostaServicoTemp || null,
    propostaServico: service?.servico
      ? `${service.servico} | ${service.quantidade} ${service.frequenciaUnidadeMedida1} / ${service.frequenciaUnidadeMedida2}`
      : null,
    precificacao: {
      tipoPrecificacao: item?.precificacao?.tipo || ProposalTipoPrecificacaoEnum.Geral,
      quantidade: item?.precificacao?.quantidade ?? null,
      quantidadeIdUnidadeMedida:
        item?.precificacao?.quantidadeIdUnidadeMedida?.toString() ||
        unitOfMeasures
          .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
          ?.idUnidadeMedida.toString() ||
        null,
      frequenciaIdUnidadeMedida: item?.precificacao?.frequenciaIdUnidadeMedida?.toString() || null,
      preco: item?.precificacao?.preco ?? null,
      imposto: item?.precificacao?.imposto ?? Financial.Tax,
    },
    observacao: item?.observacao || '',
  };
};

const buildPricingFromService = (
  service: ProposalServiceType | null,
  unitOfMeasures: UnitOfMeasureType[]
): BasicPricingData | null => {
  if (!service) {
    return null;
  }

  return {
    tipoPrecificacao: service.precificacao.tipo || ProposalTipoPrecificacaoEnum.Geral,
    quantidade: service.precificacao.quantidade ?? null,
    quantidadeIdUnidadeMedida:
      service.precificacao.quantidadeIdUnidadeMedida?.toString() ||
      unitOfMeasures
        .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
        ?.idUnidadeMedida.toString() ||
      null,
    frequenciaIdUnidadeMedida: service.precificacao.frequenciaIdUnidadeMedida?.toString() || null,
    preco: service.precificacao.preco ?? null,
    imposto: service.precificacao.imposto ?? Financial.Tax,
  };
};

export default function ResiduePlanPackagingModal(props: ResiduePlanPackagingModalProps) {
  const { onAddQuotation, opened, plan } = props;
  const refQuantity = useRef<BasicValueUnitsRef>(null);
  const refPricing = useRef<BasicPricingRef>(null);
  const quotationFormRefs = useRef<Record<string, ResidueQuotationPackagingFormRef | null>>({});
  const autoCreatedSharedQuotationRef = useRef<string | null>(null);

  let modalActionLabel = 'Adicionar';
  if (props.item) {
    modalActionLabel = props.modoEdicao ? 'Editar' : 'Visualizar';
  }

  const form = useForm<FormViewData>({
    initialValues: buildInitialValues(
      props.item,
      props.origItem,
      props.referenceData.unitOfMeasures,
      props.services,
      props.idEntidadeEndereco
    ),
    validate: {
      idResiduoAcondicionamento: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
      propostaServico: (value, values) => {
        if (values.compartilhado && (!value || value.trim() === '')) {
          return 'Campo obrigatório';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    form.setValues(
      buildInitialValues(
        props.item,
        props.origItem,
        props.referenceData.unitOfMeasures,
        props.services,
        props.idEntidadeEndereco
      )
    );
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.item, props.origItem, props.opened]);

  const quantityUnitOptions = props.referenceData.unitOfMeasures
    .filter((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const frequencyUnitOptions = props.referenceData.unitOfMeasures
    .filter(
      (x) =>
        (x.tipo === 'Tempo' &&
          (x.unidadeMedidaSigla === 'd' ||
            x.unidadeMedidaSigla === 'sem' ||
            x.unidadeMedidaSigla === 'mês' ||
            x.unidadeMedidaSigla === 'ano')) ||
        (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
    )
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const selectedSharedService = useMemo(() => {
    if (!form.values.compartilhado) {
      return null;
    }

    return (
      buildServiceFromItem(
        {
          idResiduoAcondicionamento: form.values.idResiduoAcondicionamento || 0,
          idPropostaServico: form.values.idPropostaServico,
          idPropostaServicoTemp: form.values.idPropostaServicoTemp,
        } as ProposalResiduePlanPackagingType,
        props.services,
        props.idEntidadeEndereco
      ) || form.values.propostaServicoData
    );
  }, [
    form.values.compartilhado,
    form.values.idPropostaServico,
    form.values.idPropostaServicoTemp,
    form.values.idResiduoAcondicionamento,
    form.values.propostaServicoData,
    props.idEntidadeEndereco,
    props.services,
  ]);

  const sharedServicePricing = useMemo(
    () => buildPricingFromService(selectedSharedService, props.referenceData.unitOfMeasures),
    [props.referenceData.unitOfMeasures, selectedSharedService]
  );

  const effectivePricing = sharedServicePricing || form.values.precificacao;
  const isSharedWithSelectedService = form.values.compartilhado && !!selectedSharedService;
  const displayPricingType =
    isSharedWithSelectedService || !form.values.compartilhado
      ? effectivePricing.tipoPrecificacao
      : ProposalTipoPrecificacaoEnum.Geral;

  const buildDraftRefItem = useMemo<ProposalResiduePlanPackagingType>(() => {
    const quantidadeIdUnidadeMedida = form.values.quantidadeIdUnidadeMedida
      ? Number(form.values.quantidadeIdUnidadeMedida)
      : null;
    const precificacaoQuantidadeIdUnidadeMedida = effectivePricing.quantidadeIdUnidadeMedida
      ? Number(effectivePricing.quantidadeIdUnidadeMedida)
      : null;
    const precificacaoFrequenciaIdUnidadeMedida = effectivePricing.frequenciaIdUnidadeMedida
      ? Number(effectivePricing.frequenciaIdUnidadeMedida)
      : null;

    return {
      ...buildFakeAuditObject(),
      ...(props.origItem || {}),
      ...form.values,
      id: form.values.id,
      idTemporaria: props.origItem?.idTemporaria || form.values.id,
      idPropostaResiduoPlanoAcondicionamento:
        props.origItem?.idPropostaResiduoPlanoAcondicionamento ||
        props.item?.idPropostaResiduoPlanoAcondicionamento,
      idPropostaResiduoPlano: props.idPropostaResiduoPlano || props.origItem?.idPropostaResiduoPlano || -1,
      idPropostaServico: form.values.compartilhado ? form.values.idPropostaServico : null,
      idPropostaServicoTemp: form.values.compartilhado ? form.values.idPropostaServicoTemp : null,
      quantidade: Number(form.values.quantidade || 0),
      quantidadeIdUnidadeMedida: quantidadeIdUnidadeMedida || 0,
      quantidadeUnidadeMedida:
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)
          ?.unidadeMedida || '',
      quantidadeUnidadeMedidaSigla:
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)
          ?.unidadeMedidaSigla || null,
      precificacao: {
        tipo: effectivePricing.tipoPrecificacao,
        quantidade: effectivePricing.quantidade,
        quantidadeIdUnidadeMedida: precificacaoQuantidadeIdUnidadeMedida,
        quantidadeUnidadeMedida:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoQuantidadeIdUnidadeMedida
          )?.unidadeMedida || null,
        quantidadeUnidadeMedidaSigla:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoQuantidadeIdUnidadeMedida
          )?.unidadeMedidaSigla || null,
        frequenciaIdUnidadeMedida: precificacaoFrequenciaIdUnidadeMedida,
        frequenciaUnidadeMedida:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoFrequenciaIdUnidadeMedida
          )?.unidadeMedida || null,
        frequenciaUnidadeMedidaSigla:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoFrequenciaIdUnidadeMedida
          )?.unidadeMedidaSigla || null,
        preco: effectivePricing.preco,
        imposto: effectivePricing.imposto,
      },
    } as ProposalResiduePlanPackagingType;
  }, [
    effectivePricing,
    form.values,
    props.idPropostaResiduoPlano,
    props.item,
    props.origItem,
    props.referenceData.unitOfMeasures,
  ]);

  const visibleQuotations = (props.plan?.cotacoes || []).filter(
    (quotation) => (quotation.action || Action.Nothing) !== Action.Delete
  );

  const canAddQuotation = useMemo(() => {
    if (isSharedWithSelectedService) {
      return false;
    }

    const hasPackaging = !!form.values.idResiduoAcondicionamento;
    const hasPlanQuantity =
      form.values.compartilhado ||
      (!isNullOrEmpty(form.values.quantidade) &&
        !Number.isNaN(Number(form.values.quantidade)) &&
        Number(form.values.quantidade) > 0 &&
        !!form.values.quantidadeIdUnidadeMedida);
    const hasSharedService =
      !form.values.compartilhado ||
      !!(
        form.values.propostaServicoData ||
        form.values.idPropostaServico ||
        form.values.idPropostaServicoTemp
      );
    const pricingType = form.values.compartilhado
      ? ProposalTipoPrecificacaoEnum.Geral
      : form.values.precificacao.tipoPrecificacao;
    const hasRequiredPricing =
      pricingType !== ProposalTipoPrecificacaoEnum.Geral ||
      (!isNullOrEmpty(form.values.precificacao.quantidade) &&
        !Number.isNaN(Number(form.values.precificacao.quantidade)) &&
        !!form.values.precificacao.quantidadeIdUnidadeMedida &&
        !!form.values.precificacao.frequenciaIdUnidadeMedida &&
        !isNullOrEmpty(form.values.precificacao.preco) &&
        !Number.isNaN(Number(form.values.precificacao.preco)) &&
        !isNullOrEmpty(form.values.precificacao.imposto) &&
        !Number.isNaN(Number(form.values.precificacao.imposto)));

    return hasPackaging && hasPlanQuantity && hasSharedService && hasRequiredPricing;
  }, [form.values, isSharedWithSelectedService]);

  const matchesQuotationItem = (
    quotationItem: ProposalResiduePlanQuotationPackagingType,
    refItem: ProposalResiduePlanPackagingType
  ) => {
    const refKeys = [refItem.id, refItem.idTemporaria, refItem.idPropostaResiduoPlanoAcondicionamento].filter(
      (value) => value !== null && value !== undefined && value !== ''
    );

    const quotationKeys = [
      quotationItem.id,
      quotationItem.idTemporaria,
      quotationItem.idPropostaResiduoPlanoAcondicionamento,
    ].filter((value) => value !== null && value !== undefined && value !== '');

    if (refKeys.length && quotationKeys.length) {
      return refKeys.some((refKey) =>
        quotationKeys.some((quotationKey) => `${refKey}` === `${quotationKey}`)
      );
    }

    return quotationItem.idResiduoAcondicionamento === refItem.idResiduoAcondicionamento;
  };

  const getQuotationKey = (quotation: ProposalResiduePlanQuotationType) =>
    quotation.id || quotation.idPropostaResiduoPlanoCotacao?.toString() || quotation.idTemporaria || '';

  const visiblePackagingQuotations = visibleQuotations;

  const synchronizeGeneralPricingToQuotations = (
    refItem: ProposalResiduePlanPackagingType | null | undefined
  ) => {
    if (
      !refItem ||
      refItem.precificacao?.tipo !== ProposalTipoPrecificacaoEnum.Geral ||
      !props.plan ||
      !props.onQuotationSubmit
    ) {
      return;
    }

    visiblePackagingQuotations.forEach((quotation) => {
      const quotationItem =
        quotation.acondicionamentos.find(
          (entry) =>
            (entry.action || Action.Nothing) !== Action.Delete && matchesQuotationItem(entry, refItem)
        ) || null;

      if (!quotationItem) {
        return;
      }

      props.onQuotationSubmit?.(
        quotation,
        refItem,
        {
          ...quotationItem,
          action: quotationItem.idPropostaResiduoPlanoCotacaoAcondicionamento
            ? Action.Modify
            : quotationItem.action || Action.Nothing,
          precificacao: { ...refItem.precificacao },
        },
        quotationItem.idPropostaResiduoPlanoCotacaoAcondicionamento ? Action.Modify : Action.Nothing
      );
    });
  };

  const sharedQuotationAutoCreateKey = useMemo(() => {
    if (
      !plan ||
      !onAddQuotation ||
      !isSharedWithSelectedService ||
      !buildDraftRefItem.idResiduoAcondicionamento
    ) {
      return null;
    }

    const serviceKey =
      selectedSharedService?.idPropostaServico?.toString() ||
      selectedSharedService?.id ||
      form.values.idPropostaServico?.toString() ||
      form.values.idPropostaServicoTemp ||
      '';

    if (!serviceKey) {
      return null;
    }

    return [
      plan.idPropostaResiduoPlano || plan.idTemporaria || 'plan',
      buildDraftRefItem.idPropostaResiduoPlanoAcondicionamento ||
        buildDraftRefItem.idTemporaria ||
        buildDraftRefItem.id ||
        buildDraftRefItem.idResiduoAcondicionamento,
      serviceKey,
    ].join('|');
  }, [
    buildDraftRefItem.id,
    buildDraftRefItem.idPropostaResiduoPlanoAcondicionamento,
    buildDraftRefItem.idResiduoAcondicionamento,
    buildDraftRefItem.idTemporaria,
    form.values.idPropostaServico,
    form.values.idPropostaServicoTemp,
    isSharedWithSelectedService,
    onAddQuotation,
    plan,
    selectedSharedService,
  ]);

  useEffect(() => {
    if (!sharedQuotationAutoCreateKey || !plan || !onAddQuotation) {
      return;
    }

    if (visibleQuotations.length > 0) {
      autoCreatedSharedQuotationRef.current = sharedQuotationAutoCreateKey;
      return;
    }

    if (autoCreatedSharedQuotationRef.current === sharedQuotationAutoCreateKey) {
      return;
    }

    autoCreatedSharedQuotationRef.current = sharedQuotationAutoCreateKey;
    onAddQuotation(plan);
  }, [onAddQuotation, plan, sharedQuotationAutoCreateKey, visibleQuotations.length]);

  useEffect(() => {
    if (!opened) {
      quotationFormRefs.current = {};
      autoCreatedSharedQuotationRef.current = null;
    }
  }, [opened]);

  const isModified = (quantityData?: BasicValueUnitsData | null, pricingData?: BasicPricingData | null) => {
    const origData = `${props.origItem?.idResiduoAcondicionamento || ''}|${
      Number(props.origItem?.quantidade) || ''
    }|${props.origItem?.quantidadeIdUnidadeMedida || ''}|${props.origItem?.compartilhado || ''}|${
      props.origItem?.gerenciar || ''
    }|${props.origItem?.idPropostaServico || ''}|${props.origItem?.idPropostaServicoTemp || ''}|${
      props.origItem?.precificacao?.tipo || ''
    }|${Number(props.origItem?.precificacao?.quantidade) || ''}|${
      props.origItem?.precificacao?.quantidadeIdUnidadeMedida || ''
    }|${props.origItem?.precificacao?.frequenciaIdUnidadeMedida || ''}|${
      Number(props.origItem?.precificacao?.preco) || ''
    }|${Number(props.origItem?.precificacao?.imposto) || ''}|${props.origItem?.observacao || ''}`;

    const formData = `${form.values.idResiduoAcondicionamento || ''}|${
      Number(form.values.compartilhado ? form.values.quantidade : quantityData?.valor) || ''
    }|${
      form.values.compartilhado ? form.values.quantidadeIdUnidadeMedida || '' : quantityData?.idUnidade1 || ''
    }|${form.values.compartilhado || ''}|${form.values.gerenciar || ''}|${
      form.values.idPropostaServico || ''
    }|${form.values.idPropostaServicoTemp || ''}|${pricingData?.tipoPrecificacao || ''}|${
      Number(pricingData?.quantidade) || ''
    }|${pricingData?.quantidadeIdUnidadeMedida || ''}|${pricingData?.frequenciaIdUnidadeMedida || ''}|${
      Number(pricingData?.preco) || ''
    }|${Number(pricingData?.imposto) || ''}|${form.values.observacao || ''}`;

    return origData !== formData;
  };

  const savePlanItem = async (
    action = 'callback',
    closeOnSuccess = false
  ): Promise<ProposalResiduePlanPackagingType | null> => {
    if (!props.modoEdicao) {
      if (closeOnSuccess) {
        props.onClose();
      }
      return null;
    }

    const quantityData = form.values.compartilhado ? null : refQuantity.current?.validate();
    const pricingData = refPricing.current?.validate();

    if (form.validate().hasErrors || (!form.values.compartilhado && !quantityData) || !pricingData) {
      return null;
    }

    let values = { ...form.values };
    if (props.origItem) {
      if (isModified(quantityData, pricingData) && props.origItem.idPropostaResiduoPlanoAcondicionamento) {
        values.action = Action.Modify;
      } else if (values.compartilhado) {
        values = {
          ...props.origItem,
          ...values,
          action: props.item?.action || Action.Nothing,
          quantidadeIdUnidadeMedida: form.values.quantidadeIdUnidadeMedida?.toString() || '',
          compartilhado: true,
          idPropostaServico: values.idPropostaServico,
          idPropostaServicoTemp: values.idPropostaServicoTemp,
        };
      } else {
        values = {
          ...props.origItem,
          ...values,
          action: props.item?.action || Action.Nothing,
          quantidadeIdUnidadeMedida: quantityData?.idUnidade1 || '',
        };
      }
    }

    const formItem: ProposalResiduePlanPackagingType = {
      ...buildFakeAuditObject(),
      ...(props.origItem || {}),
      ...values,
      action: values.action,
      id: values.id,
      idTemporaria: props.origItem?.idTemporaria || values.id,
      idPropostaResiduoPlano: props.idPropostaResiduoPlano || props.origItem?.idPropostaResiduoPlano || -1,
      quantidade: form.values.compartilhado
        ? Number(values.propostaServicoData?.quantidade)
        : Number(quantityData?.valor),
      quantidadeIdUnidadeMedida:
        (form.values.compartilhado ? form.values.quantidadeIdUnidadeMedida : quantityData?.idUnidade1) || '',
      precificacao: {
        tipo: pricingData.tipoPrecificacao,
        quantidade: pricingData.quantidade,
        quantidadeIdUnidadeMedida: pricingData.quantidadeIdUnidadeMedida
          ? Number(pricingData.quantidadeIdUnidadeMedida)
          : null,
        quantidadeUnidadeMedida: null,
        quantidadeUnidadeMedidaSigla: null,
        frequenciaIdUnidadeMedida: pricingData.frequenciaIdUnidadeMedida
          ? Number(pricingData.frequenciaIdUnidadeMedida)
          : null,
        frequenciaUnidadeMedida: null,
        frequenciaUnidadeMedidaSigla: null,
        preco: pricingData.preco,
        imposto: pricingData.imposto,
      },
    };

    formItem.quantidadeIdUnidadeMedida = Number(formItem.quantidadeIdUnidadeMedida);
    formItem.quantidadeUnidadeMedida =
      props.referenceData.unitOfMeasures.find(
        (x) =>
          x.idUnidadeMedida ===
          Number(form.values.compartilhado ? form.values.quantidadeIdUnidadeMedida : quantityData?.idUnidade1)
      )?.unidadeMedida || '';

    formItem.precificacao.quantidadeUnidadeMedida =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.quantidadeIdUnidadeMedida
      )?.unidadeMedida || null;
    formItem.precificacao.quantidadeUnidadeMedidaSigla =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.quantidadeIdUnidadeMedida
      )?.unidadeMedidaSigla || null;
    formItem.precificacao.frequenciaUnidadeMedida =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.frequenciaIdUnidadeMedida
      )?.unidadeMedida || null;
    formItem.precificacao.frequenciaUnidadeMedidaSigla =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.frequenciaIdUnidadeMedida
      )?.unidadeMedidaSigla || null;

    if (!formItem.compartilhado) {
      formItem.idPropostaServico = null;
      formItem.idPropostaServicoTemp = null;
    }

    formItem.observacao = formItem.observacao?.trim() || null;
    const callbackResult = await props.callback?.(formItem, action, true);
    const savedItem = callbackResult || formItem;

    synchronizeGeneralPricingToQuotations(savedItem);

    if (closeOnSuccess) {
      props.onClose();
    }

    return savedItem;
  };

  const handleSubmit = async (action = 'callback') => {
    if (!props.modoEdicao) {
      props.onClose();
      return;
    }

    const savedPlanItem = await savePlanItem(action, false);

    if (!savedPlanItem) {
      return;
    }

    if (isSharedWithSelectedService) {
      props.onClose();
      return;
    }

    const submitResults = await visiblePackagingQuotations.reduce<Promise<(boolean | undefined)[]>>(
      async (promiseAcc, quotation) => {
        const acc = await promiseAcc;
        const result = await quotationFormRefs.current[getQuotationKey(quotation)]?.submit('autosave');
        acc.push(result);
        return acc;
      },
      Promise.resolve([])
    );

    if (submitResults.some((result) => result === false)) {
      return;
    }

    props.onClose();
  };

  return (
    <Modal
      opened={props.opened}
      onClose={props.onClose}
      title={`Plano/Acondicionamento - ${modalActionLabel}`}
      size="75%"
      zIndex={props.zIndex}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Paper shadow="xs" p="md" withBorder>
        <PageSection
          size="lg"
          color={Feature.Home.Proposal.color}
          label="Plano de Acondicionamento"
          text="Defina o acondicionamento, a quantidade operacional, o compartilhamento e as observações deste item."
        />
        <Space h="xs" />
        <Grid columns={6}>
          <Grid.Col span={3}>
            <PackagingPicker
              label="Acondicionamento"
              formItem={form.getInputProps('idResiduoAcondicionamento')}
              idsToBeDisabled={[]}
              callback={(item: PackagingType | null) => {
                if (item) {
                  form.setFieldValue('idResiduoAcondicionamento', item.idResiduoAcondicionamento);
                  form.setFieldValue('residuoAcondicionamento', item.residuoAcondicionamento);
                  form.setFieldValue('residuoAcondicionamentoDescricao', item.descricao);
                  if (form.values.compartilhado) {
                    form.setFieldValue('propostaServicoData', null);
                    form.setFieldValue('idPropostaServico', null);
                    form.setFieldValue('idPropostaServicoTemp', null);
                    form.setFieldValue('propostaServico', '');
                  }
                }
              }}
              disabled={!props.modoEdicao}
              zIndex={props.zIndex + 1}
              useCache
            />
          </Grid.Col>
          <Grid.Col span={3} hidden={form.values.compartilhado}>
            <BasicValueUnits
              ref={refQuantity}
              isEdit={props.modoEdicao}
              disabled={!props.modoEdicao}
              disableUnit1
              zIndex={props.zIndex + 1}
              label="Quantidade"
              showUnit2={false}
              validationMode="all"
              valueMin={1}
              valueDefaultValue={1}
              valueStep={1}
              valueMustBePositiveWhenFilled
              value={{
                valor: form.values.quantidade,
                idUnidade1: form.values.quantidadeIdUnidadeMedida,
                idUnidade2: null,
              }}
              onChange={(quantityData) => {
                form.setFieldValue('quantidade', quantityData.valor);
                form.setFieldValue('quantidadeIdUnidadeMedida', quantityData.idUnidade1);
              }}
              unit1Options={quantityUnitOptions}
            />
          </Grid.Col>
        </Grid>
        <Space h="xs" />
        <Grid columns={6}>
          <Grid.Col span={2}>
            <div>
              <Input.Wrapper label="Compartilhado?">
                <div />
              </Input.Wrapper>
              <Switch
                mt={8}
                disabled={!props.modoEdicao}
                checked={form.values.compartilhado}
                onChange={(event) => {
                  const { checked } = event.currentTarget;
                  form.setFieldValue('compartilhado', checked);

                  if (!checked) {
                    form.setFieldValue('propostaServicoData', null);
                    form.setFieldValue('idPropostaServico', null);
                    form.setFieldValue('idPropostaServicoTemp', null);
                    form.setFieldValue('propostaServico', '');
                  }
                }}
              />
            </div>
          </Grid.Col>
          <Grid.Col span={2}>
            <div>
              <Input.Wrapper label="Gerenciar?">
                <div />
              </Input.Wrapper>
              <Switch
                mt={8}
                disabled={!props.modoEdicao}
                {...form.getInputProps('gerenciar', { type: 'checkbox' })}
              />
            </div>
          </Grid.Col>
          <Grid.Col span={2} hidden={!form.values.compartilhado}>
            <ProposalServiceList
              label="Serviço compartilhado"
              tooltip="O serviço deve referenciar o mesmo acondicionamento, ser no mesmo endereço do resíduo, ter uma cotação primária e ser compartilhado."
              referenceData={{
                idProposta: null,
                servicos: props.services.filter(
                  (x) =>
                    x.action !== Action.Delete &&
                    validarReferenciaServicoCompartilhado(
                      form.values.idResiduoAcondicionamento || -1,
                      props.idEntidadeEndereco,
                      x
                    )
                ),
              }}
              formItem={form.getInputProps('propostaServico')}
              idPropostaServico={form.values.idPropostaServico}
              propostaServico={form.values.propostaServico}
              idsToBeDisabled={[]}
              zIndex={props.zIndex + 1}
              callback={(item: ProposalServiceType | null) => {
                form.setFieldValue('propostaServicoData', item);
                form.setFieldValue('idPropostaServico', item?.idPropostaServico || null);
                form.setFieldValue('idPropostaServicoTemp', item?.id || null);
                form.setFieldValue(
                  'propostaServico',
                  item?.servico
                    ? `${item.servico} | ${item.quantidade} ${item.frequenciaUnidadeMedida1} / ${item.frequenciaUnidadeMedida2}`
                    : ''
                );
              }}
              disabled={!props.modoEdicao}
            />
          </Grid.Col>
        </Grid>
        <Space h="xs" />
        <Textarea
          leftSection={<IconNotes size={15} />}
          label="Observação"
          placeholder="Observação sobre o acondicionamento"
          autosize
          maxLength={500}
          disabled={!props.modoEdicao}
          {...form.getInputProps('observacao')}
        />
      </Paper>
      <Space h="xs" />
      {isSharedWithSelectedService && (
        <>
          <Alert icon={<IconAlertTriangle size={16} />} title="Acondicionamento Compartilhado" color="yellow">
            A precificação do cliente e as cotações são herdadas do serviço compartilhado selecionado. Os
            valores abaixo são exibidos para conferência apenas.
          </Alert>
          <Space h="xs" />
        </>
      )}
      <Paper shadow="xs" p="md" withBorder>
        <PageSection
          size="lg"
          color={Feature.Home.Proposal.color}
          label="Precificação do Cliente"
          text="Representa os valores que serão cobrados do cliente para este item do plano."
        />
        <Space h="xs" />
        <BasicPricing
          ref={refPricing}
          isEdit={props.modoEdicao}
          disabled={!props.modoEdicao || isSharedWithSelectedService}
          zIndex={props.zIndex + 1}
          quantityMin={0}
          quantityDefaultValue={0}
          quantityUnitOptions={quantityUnitOptions}
          quantityUnitDisabled
          withFrequency
          frequencyUnitOptions={frequencyUnitOptions}
          value={{
            ...effectivePricing,
            tipoPrecificacao: displayPricingType,
          }}
          onChange={(pricingData) => {
            form.setFieldValue('precificacao', pricingData);
          }}
        />
      </Paper>
      <Space h="xs" />

      <Paper shadow="xs" p="md" withBorder>
        <Group justify="space-between">
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Cotações"
            text="Cadastre e revise as cotações deste item do plano."
          />
          {props.modoEdicao && props.plan && props.onAddQuotation && (
            <Button
              type="button"
              size="xs"
              leftSection={<IconPlus size={16} />}
              disabled={!canAddQuotation}
              onClick={() => {
                if (props.plan) {
                  props.onAddQuotation?.(props.plan);
                }
              }}
            >
              Adicionar cotação
            </Button>
          )}
        </Group>

        <Space h="xs" />

        {!buildDraftRefItem.idResiduoAcondicionamento && (
          <Text size="sm" c="dimmed">
            Selecione o acondicionamento do item do plano para cadastrar fornecedores.
          </Text>
        )}

        {isSharedWithSelectedService && (
          <Text size="sm" c="dimmed">
            As cotações deste acondicionamento seguem automaticamente a cotação primária do serviço
            compartilhado selecionado.
          </Text>
        )}

        {!!buildDraftRefItem.idResiduoAcondicionamento &&
          !isSharedWithSelectedService &&
          !visiblePackagingQuotations.length && (
            <Text size="sm" c="dimmed">
              Nenhuma cotação disponível para este plano.
            </Text>
          )}

        {!!buildDraftRefItem.idResiduoAcondicionamento &&
          visiblePackagingQuotations.map((quotation) => {
            const quotationKey = getQuotationKey(quotation);
            const quotationItem =
              quotation.acondicionamentos.find(
                (entry) =>
                  (entry.action || Action.Nothing) !== Action.Delete &&
                  matchesQuotationItem(entry, buildDraftRefItem)
              ) || null;

            return (
              <Paper key={quotationKey} shadow="xs" p="md" withBorder mt="sm">
                <Group justify="space-between">
                  <Group gap="xs">
                    <Text fw={500}>Cotação #{quotation.idPropostaResiduoPlanoCotacao || '-'}</Text>
                    {quotation.primario && (
                      <Badge color="green" variant="light">
                        Primária
                      </Badge>
                    )}
                  </Group>

                  <Group gap="xs">
                    {props.modoEdicao && props.plan && props.onSetPrimaryQuotation && !quotation.primario && (
                      <Tooltip label="Definir como primária" withArrow>
                        <ActionIcon
                          size="sm"
                          color="green"
                          variant="light"
                          disabled={!quotation.completo || !quotationItem}
                          onClick={() => {
                            if (props.plan) {
                              props.onSetPrimaryQuotation?.(props.plan, quotation);
                            }
                          }}
                        >
                          <IconCheck size={15} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {props.modoEdicao && props.plan && props.onDeleteQuotation && (
                      <DeleteConfirmPopover
                        label="Excluir cotação"
                        message="Esta ação removerá a cotação do item do plano."
                        zIndex={props.zIndex + 1}
                        onConfirm={() => {
                          if (props.plan) {
                            props.onDeleteQuotation?.(props.plan, quotation);
                          }
                        }}
                      />
                    )}
                  </Group>
                </Group>

                {props.onQuotationSubmit && (
                  <>
                    <Space h="xs" />
                    <ResidueQuotationPackagingForm
                      ref={(instance) => {
                        quotationFormRefs.current[quotationKey] = instance;
                      }}
                      modoEdicao={props.modoEdicao}
                      requireSupplier={props.requireSupplier}
                      referenceData={props.referenceData}
                      services={props.services}
                      origItem={quotationItem}
                      item={quotationItem}
                      refItem={buildDraftRefItem}
                      idPropostaResiduoPlanoCotacao={quotation.idPropostaResiduoPlanoCotacao}
                      zIndex={props.zIndex + 1}
                      hideCustomerSection={
                        !isSharedWithSelectedService &&
                        buildDraftRefItem.precificacao?.tipo === ProposalTipoPrecificacaoEnum.Geral
                      }
                      submitLabel="Salvar cotação"
                      callback={async (item, refItem, action, confirmed) => {
                        if (!confirmed || !item || !refItem) {
                          return;
                        }

                        const savedRefItem = await savePlanItem('autosave', false);

                        if (!savedRefItem) {
                          return;
                        }

                        props.onQuotationSubmit?.(quotation, savedRefItem, item, action);
                        showNotification({
                          title: 'Plano/Acondicionamento - Cotação',
                          message: 'Cotação salva com sucesso.',
                          color: 'green',
                        });
                      }}
                    />
                  </>
                )}
              </Paper>
            );
          })}
      </Paper>

      <Space h="xl" />
      <Group justify="right">
        {props.modoEdicao ? (
          <Button type="button" leftSection={<IconDeviceFloppy size={18} />} onClick={() => handleSubmit()}>
            Salvar
          </Button>
        ) : (
          <Button type="button" onClick={props.onClose}>
            Fechar
          </Button>
        )}
      </Group>
    </Modal>
  );
}
